// PROMPT 145 (2026-10-21): avisos de WhatsApp en toda la app.
// Los numeros de WhatsApp de las marcas no estan en ningun celular: la plataforma es el unico
// lugar donde llegan los mensajes, asi que tiene que avisar como avisa WhatsApp. Se monta UNA vez
// en AppLayout (administradores y operadores) y comparte por contexto:
//  - el total de no leidos (menu lateral, titulo de la pestaña y pagina del agente);
//  - los eventos "wa_*" del WebSocket de la agenda (/api/ws/agenda, PROMPT 127), para que la
//    pagina del agente se actualice sola (`suscribir`);
//  - las preferencias de ESTE navegador: sonido, avisos del sistema y de cuales conversaciones.
// Todo lo que se guarda en el navegador va dentro de try/catch (puede no haber almacenamiento).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Volume2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAgendaSocket } from "@/hooks/useAgendaSocket";
import { nombreCorto, nombreVisible } from "@/lib/marcasWa";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const CLAVE_PREFS = "tny_wa_avisos";
const PREFS_INICIALES = { sonido: true, escritorio: false, cuales: "todas" };
const PREFIJO_TITULO = /^\(\d+\)\s+/;

function leerPrefs() {
  try {
    const crudo = window.localStorage.getItem(CLAVE_PREFS);
    if (!crudo) return PREFS_INICIALES;
    const p = JSON.parse(crudo) || {};
    return {
      sonido: p.sonido !== false,
      escritorio: p.escritorio === true,
      cuales: p.cuales === "mias" ? "mias" : "todas",
    };
  } catch {
    return PREFS_INICIALES;
  }
}

function guardarPrefs(prefs) {
  try {
    window.localStorage.setItem(CLAVE_PREFS, JSON.stringify(prefs));
  } catch {
    /* sin almacenamiento: las preferencias valen mientras la pestaña siga abierta */
  }
}

const hayNotificaciones = () => typeof window !== "undefined" && "Notification" in window;
const permisoActual = () => (hayNotificaciones() ? window.Notification.permission : "no-soportado");

// ---------------------------------------------------------------------------- sonido
// Dos tonos cortos hechos con WebAudio: no hay archivo de sonido que bajar ni que se pierda.
let contextoAudio = null;

function prepararAudio() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    if (!contextoAudio) contextoAudio = new AC();
    if (contextoAudio.state === "suspended") contextoAudio.resume();
    return contextoAudio;
  } catch {
    return null;
  }
}

export function sonar() {
  const ctx = prepararAudio();
  if (!ctx) return;
  try {
    const t0 = ctx.currentTime;
    [660, 880].forEach((frecuencia, i) => {
      const inicio = t0 + i * 0.16;
      const osc = ctx.createOscillator();
      const ganancia = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = frecuencia;
      ganancia.gain.setValueAtTime(0.0001, inicio);
      ganancia.gain.exponentialRampToValueAtTime(0.18, inicio + 0.02);
      ganancia.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.22);
      osc.connect(ganancia);
      ganancia.connect(ctx.destination);
      osc.start(inicio);
      osc.stop(inicio + 0.25);
    });
  } catch {
    /* sin audio: el aviso visual alcanza */
  }
}

// ---------------------------------------------------------------------------- el aviso
const ROTULOS_ARCHIVO = {
  image: "📷 Foto",
  imagen: "📷 Foto",
  audio: "🎤 Audio",
  voice: "🎤 Audio",
  video: "🎥 Video",
  document: "📄 Documento",
  documento: "📄 Documento",
  sticker: "Sticker",
  location: "📍 Ubicación",
  ubicacion: "📍 Ubicación",
  contacts: "👤 Contacto",
  contacto: "👤 Contacto",
};

// Hasta 100 caracteres del mensaje; si es un archivo, lo que es ("📷 Foto").
export function textoAviso(msg) {
  const tipo = String(msg?.tipo || "text").toLowerCase();
  if (ROTULOS_ARCHIVO[tipo]) return ROTULOS_ARCHIVO[tipo];
  const texto = String(msg?.texto || "").trim();
  const legado = /^\[([a-z]+)\]$/i.exec(texto);
  if (legado && ROTULOS_ARCHIVO[legado[1].toLowerCase()]) {
    return ROTULOS_ARCHIVO[legado[1].toLowerCase()];
  }
  return texto ? texto.slice(0, 100) : "Mensaje nuevo";
}

// La conversacion esta abierta en una pestaña que se ve y tiene el foco: ahi la pagina la marca
// leida y no hace falta avisar.
function estaAbiertaYVisible(ubicacion, id) {
  if (!ubicacion.pathname.startsWith("/agente")) return false;
  if (new URLSearchParams(ubicacion.search).get("c") !== id) return false;
  if (document.visibilityState !== "visible") return false;
  return typeof document.hasFocus !== "function" || document.hasFocus();
}

// "Avisarme de": todas, o solo las mias y las que nadie tiene asignadas.
function lesToca(conv, prefs, usuario) {
  if (prefs.cuales !== "mias") return true;
  return !conv.asignada_a || conv.asignada_a === usuario?.id;
}

function mostrarAviso(conv, msg, alAbrir) {
  if (permisoActual() !== "granted") return;
  try {
    const aviso = new window.Notification(`${nombreCorto(conv.marca_slug, [])} · ${nombreVisible(conv)}`, {
      body: textoAviso(msg),
      tag: conv.id, // reemplaza el aviso anterior de ese mismo chat
    });
    aviso.onclick = () => {
      try {
        window.focus();
      } catch {
        /* el navegador no deja enfocar: igual se abre el chat */
      }
      alAbrir(conv.id);
      aviso.close();
    };
  } catch {
    /* hay navegadores que solo muestran avisos desde un service worker */
  }
}

// ---------------------------------------------------------------------------- contexto
const SIN_AVISOS = {
  activo: false,
  total: 0,
  porMarca: {},
  prefs: PREFS_INICIALES,
  permiso: "no-soportado",
  cambiarPrefs: () => {},
  pedirPermiso: async () => "no-soportado",
  actualizarPermiso: () => {},
  probarSonido: () => {},
  suscribir: () => () => {},
  refrescar: () => {},
};

const AvisosContext = createContext(null);

// Sin el proveedor (pantallas que no son del equipo) devuelve valores vacios que no hacen nada.
export function useAvisosWa() {
  return useContext(AvisosContext) || SIN_AVISOS;
}

export default function AvisosWhatsApp({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [total, setTotal] = useState(0);
  const [porMarca, setPorMarca] = useState({});
  const [prefs, setPrefs] = useState(leerPrefs);
  const [permiso, setPermiso] = useState(permisoActual);

  const suscriptores = useRef(new Set());
  const pedido = useRef(0);
  const reloj = useRef(null);
  // Lo que el manejador del WebSocket tiene que ver "ahora" sin reabrir el socket.
  const ahora = useRef({});
  ahora.current = { location, prefs, user, navigate };

  const refrescar = useCallback(async () => {
    const mio = ++pedido.current;
    try {
      const { data } = await api.get("/whatsapp/estado");
      if (mio !== pedido.current) return;
      setTotal(Number(data?.no_leidos_total) || 0);
      setPorMarca(data?.no_leidos_por_marca || {});
    } catch {
      /* sin conexion: se corrige con el proximo evento o al volver a la pestaña */
    }
  }, []);

  // Ante un cambio, el numero se vuelve a pedir al servidor con un segundo de espera.
  const refrescarEnUnSegundo = useCallback(() => {
    clearTimeout(reloj.current);
    reloj.current = setTimeout(refrescar, 1000);
  }, [refrescar]);

  const alEvento = useCallback(
    (ev) => {
      if (!ev || typeof ev.tipo !== "string" || !ev.tipo.startsWith("wa_")) return;
      suscriptores.current.forEach((fn) => {
        try {
          fn(ev);
        } catch {
          /* un suscriptor roto no frena a los demas */
        }
      });
      if (ev.tipo === "wa_conversacion") {
        refrescarEnUnSegundo();
        return;
      }
      if (ev.tipo !== "wa_mensaje") return;
      refrescarEnUnSegundo();
      const { conversacion: conv, mensaje: msg } = ev;
      // Solo un mensaje NUEVO de un cliente en una conversacion de WhatsApp real: el simulador,
      // lo que escribe el equipo y la edicion de un mensaje no suenan ni avisan.
      if (ev.evento !== "nuevo" || msg?.direccion !== "entrante") return;
      if (msg?.autor !== "cliente" || conv?.canal !== "whatsapp") return;
      const { location: ubicacion, prefs: preferencias, user: usuario, navigate: ir } = ahora.current;
      if (estaAbiertaYVisible(ubicacion, conv.id)) return;
      if (conv.marca_slug) {
        setTotal((n) => n + 1);
        setPorMarca((m) => ({ ...m, [conv.marca_slug]: (m[conv.marca_slug] || 0) + 1 }));
      }
      if (!lesToca(conv, preferencias, usuario)) return;
      if (preferencias.sonido) sonar();
      if (preferencias.escritorio) {
        mostrarAviso(conv, msg, (id) => ir(`/agente?c=${encodeURIComponent(id)}`));
      }
    },
    [refrescarEnUnSegundo]
  );

  useAgendaSocket(alEvento);

  useEffect(() => {
    refrescar();
    const alVolver = () => {
      if (document.visibilityState === "visible") refrescarEnUnSegundo();
    };
    document.addEventListener("visibilitychange", alVolver);
    window.addEventListener("focus", alVolver);
    // Respaldo por si el WebSocket estuvo caido un rato.
    const respaldo = setInterval(() => {
      if (document.visibilityState === "visible") refrescar();
    }, 120000);
    return () => {
      document.removeEventListener("visibilitychange", alVolver);
      window.removeEventListener("focus", alVolver);
      clearInterval(respaldo);
      clearTimeout(reloj.current);
    };
  }, [refrescar, refrescarEnUnSegundo]);

  // Los navegadores no dejan sonar hasta que la persona toca la pagina una vez.
  useEffect(() => {
    const desbloquear = () => {
      prepararAudio();
    };
    window.addEventListener("pointerdown", desbloquear, { once: true });
    window.addEventListener("keydown", desbloquear, { once: true });
    return () => {
      window.removeEventListener("pointerdown", desbloquear);
      window.removeEventListener("keydown", desbloquear);
    };
  }, []);

  // Titulo de la pestaña: "(N) " adelante cuando hay no leidos.
  useEffect(() => {
    const limpio = document.title.replace(PREFIJO_TITULO, "");
    document.title = total > 0 ? `(${total}) ${limpio}` : limpio;
  }, [total, location.pathname]);
  useEffect(
    () => () => {
      document.title = document.title.replace(PREFIJO_TITULO, "");
    },
    []
  );

  const suscribir = useCallback((fn) => {
    suscriptores.current.add(fn);
    return () => {
      suscriptores.current.delete(fn);
    };
  }, []);

  const cambiarPrefs = useCallback((parcial) => {
    setPrefs((antes) => {
      const nuevas = { ...antes, ...parcial };
      guardarPrefs(nuevas);
      return nuevas;
    });
  }, []);

  const actualizarPermiso = useCallback(() => setPermiso(permisoActual()), []);

  // Activar los avisos del sistema: pide el permiso al navegador (tiene que salir de un clic).
  const pedirPermiso = useCallback(async () => {
    if (!hayNotificaciones()) {
      setPermiso("no-soportado");
      return "no-soportado";
    }
    let resultado = permisoActual();
    try {
      resultado = (await window.Notification.requestPermission()) || permisoActual();
    } catch {
      resultado = permisoActual();
    }
    setPermiso(resultado);
    if (resultado === "granted") cambiarPrefs({ escritorio: true });
    return resultado;
  }, [cambiarPrefs]);

  const valor = useMemo(
    () => ({
      activo: true,
      total,
      porMarca,
      prefs,
      permiso,
      cambiarPrefs,
      pedirPermiso,
      actualizarPermiso,
      probarSonido: sonar,
      suscribir,
      refrescar,
    }),
    [total, porMarca, prefs, permiso, cambiarPrefs, pedirPermiso, actualizarPermiso, suscribir, refrescar]
  );

  return <AvisosContext.Provider value={valor}>{children}</AvisosContext.Provider>;
}

// ---------------------------------------------------------------------------- "Avisos en esta computadora"
// El panel del menu "⋯" de la pagina del agente (las preferencias se guardan en este navegador).
export function AvisosEscritorioDialog({ open, onOpenChange }) {
  const { prefs, cambiarPrefs, permiso, pedirPermiso, actualizarPermiso, probarSonido } =
    useAvisosWa();

  useEffect(() => {
    if (open) actualizarPermiso();
  }, [open, actualizarPermiso]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="wa-avisos-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Avisos en esta computadora</DialogTitle>
          <DialogDescription>
            Valen para este navegador. Con la plataforma abierta en cualquier pestaña, te avisa cuando
            un cliente escribe.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 text-sm text-slate-700">
          <section className="space-y-2">
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={prefs.sonido}
                onChange={(e) => cambiarPrefs({ sonido: e.target.checked })}
                className="mt-0.5 h-4 w-4"
                data-testid="wa-avisos-sonido"
              />
              <span>Un sonido corto cuando llega un mensaje</span>
            </label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={probarSonido}
              data-testid="wa-avisos-probar"
              className="gap-1.5"
            >
              <Volume2 className="h-4 w-4" /> Probar sonido
            </Button>
          </section>

          <section className="space-y-2">
            <p className="font-medium text-slate-900">Avisos del sistema</p>
            {permiso === "no-soportado" && (
              <p data-testid="wa-avisos-no-soportado">Este navegador no permite avisos del sistema.</p>
            )}
            {permiso === "denied" && (
              <p
                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800"
                data-testid="wa-avisos-bloqueados"
              >
                Los avisos están bloqueados en este navegador. Para habilitarlos: tocá el candado (o el
                ícono de ajustes) a la izquierda de la dirección, entrá en “Notificaciones”, elegí
                “Permitir” y recargá la página.
              </p>
            )}
            {permiso === "default" && (
              <Button
                type="button"
                size="sm"
                onClick={pedirPermiso}
                data-testid="wa-avisos-activar"
              >
                Activar avisos del sistema
              </Button>
            )}
            {permiso === "granted" && (
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={prefs.escritorio}
                  onChange={(e) => cambiarPrefs({ escritorio: e.target.checked })}
                  className="mt-0.5 h-4 w-4"
                  data-testid="wa-avisos-escritorio"
                />
                <span>Mostrar un aviso del sistema con cada mensaje nuevo</span>
              </label>
            )}
          </section>

          <section className="space-y-1.5">
            <label htmlFor="wa-avisos-cuales" className="font-medium text-slate-900">
              Avisarme de
            </label>
            <select
              id="wa-avisos-cuales"
              value={prefs.cuales}
              onChange={(e) => cambiarPrefs({ cuales: e.target.value })}
              data-testid="wa-avisos-cuales"
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-base text-slate-700 sm:text-sm"
            >
              <option value="todas">Todas las conversaciones</option>
              <option value="mias">Las mías y las sin asignar</option>
            </select>
            <p className="text-xs text-slate-500">
              El número de no leídos del menú cuenta siempre todas.
            </p>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
